import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, DotsThreeVertical } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { MerchantSelect } from "@/components/MerchantSelect";
import { api } from "@/lib/api";
import { Field, Modal } from "@/pages/Merchants";

const FEE_TYPE = "card_issuance";

const DEFAULT_LIMIT = 25;

const emptyForm = () => ({
  merchant_id: "",
  fee_type: FEE_TYPE,
  amount: 0,
  free_cards: 0,
});

export default function Fees() {
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/fees", { params: { page, limit, fee_type: FEE_TYPE } });
      const allRows = r.data.items || [];
      const rows = allRows.filter((f) => f.fee_type === FEE_TYPE);
      const serverFiltered = allRows.length === 0 || rows.length === allRows.length;
      const count = serverFiltered ? (r.data.total ?? rows.length) : rows.length;
      setItems(rows);
      setTotal(count);
      setPages(
        serverFiltered
          ? (r.data.pages ?? (count > 0 ? Math.ceil(count / limit) : 0))
          : (rows.length > 0 ? 1 : 0),
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/admin/merchants", { params: { limit: 500 } }).then((r) => setMerchants(r.data.items));
  }, []);

  const openCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      merchant_id: f.merchant_id || "",
      fee_type: f.fee_type,
      amount: f.amount,
      free_cards: f.free_cards ?? 0,
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/fees/${editing.id}`, {
          amount: Number(form.amount),
          percentage: 0,
          free_cards: Number(form.free_cards) || 0,
          grace_form_factor: "any",
        });
        toast.success("Fee updated");
      } else {
        await api.post("/admin/fees", {
          merchant_id: form.merchant_id || null,
          card_type_id: null,
          fee_type: FEE_TYPE,
          amount: Number(form.amount),
          percentage: 0,
          free_cards: Number(form.free_cards) || 0,
          grace_form_factor: "any",
        });
        toast.success("Fee created");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (f) => {
    try {
      await api.patch(`/admin/fees/${f.id}`, { active: !f.active });
      toast.success(f.active ? "Fee disabled" : "Fee enabled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const merchantsById = Object.fromEntries(merchants.map((m) => [m.id, m.company_name]));

  const scopeLabel = (f) => (
    f.merchant_id
      ? merchantsById[f.merchant_id] || "merchant"
      : <span className="text-zinc-400">All merchants</span>
  );

  const freeCardsLabel = (f) => {
    if (!f.free_cards) return "—";
    return String(Number(f.free_cards));
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="fees-page">
        <PageHeader
          title="Fees"
          subtitle="Configure card issuance fees per merchant."
          actions={
            <button onClick={openCreate} data-testid="create-fee-btn" className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150">
              <Plus size={14} weight="bold" /> New fee rule
            </button>
          }
        />

        <Surface>
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Scope</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Free cards</th>
                <th className="px-5 py-3">Active</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-xs text-zinc-500">Loading…</td>
                </tr>
              ) : items.map((f) => (
                <tr key={f.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs">{scopeLabel(f)}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">${Number(f.amount).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{freeCardsLabel(f)}</td>
                  <td className="px-5 py-3"><StatusPill value={f.active ? "active" : "suspended"} /></td>
                  <td className="px-5 py-3 text-right">
                    <FeeActionsMenu
                      fee={f}
                      open={openMenuId === f.id}
                      onOpenChange={(next) => setOpenMenuId(next ? f.id : null)}
                      onEdit={() => { setOpenMenuId(null); openEdit(f); }}
                      onToggle={() => { setOpenMenuId(null); toggle(f); }}
                    />
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-xs text-zinc-500">No fee rules.</td></tr>}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={(next) => {
              setLimit(next);
              setPage(1);
            }}
          />
        </Surface>

        {open && (
          <Modal title={editing ? "Edit fee rule" : "New fee rule"} onClose={closeModal}>
            <form onSubmit={submit} className="space-y-3">
              {editing ? (
                <>
                  <div className="text-sm text-zinc-600 rounded-sm border border-zinc-100 bg-zinc-50 px-3 py-2">
                    <span className="text-zinc-500">Scope:</span> {scopeLabel(editing)}
                  </div>
                  <Field label="Fixed amount (USD)" testId="fee-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
                  <Field
                    label="Free cards"
                    testId="fee-free-cards"
                    value={form.free_cards}
                    onChange={(v) => setForm({ ...form, free_cards: v })}
                  />
                </>
              ) : (
                <>
                  <MerchantSelect
                    value={form.merchant_id}
                    onChange={(id) => setForm({ ...form, merchant_id: id })}
                    testId="fee-merchant"
                  />
                  <Field label="Fixed amount (USD)" testId="fee-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
                  <Field
                    label="Free cards"
                    testId="fee-free-cards"
                    value={form.free_cards}
                    onChange={(v) => setForm({ ...form, free_cards: v })}
                  />
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">Cancel</button>
                <button data-testid="submit-fee" className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150">
                  {editing ? "Save changes" : "Create"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

function FeeActionsMenu({ fee, open, onOpenChange, onEdit, onToggle }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 160;
    const menuHeight = 88;
    const gap = 4;
    let top = rect.bottom + gap;
    let left = rect.right - menuWidth;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setMenuStyle({ position: "fixed", top, left, width: menuWidth, zIndex: 50 });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Fee actions"
        aria-expanded={open}
        data-testid={`fee-actions-${fee.id}`}
        onClick={() => onOpenChange(!open)}
        className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors duration-150"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && menuStyle && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={menuStyle}
          className="min-w-[10rem] rounded-sm border border-zinc-200 bg-white py-1 shadow-lg"
          data-testid={`fee-actions-menu-${fee.id}`}
        >
          <FeeMenuItem onClick={onEdit} testId={`edit-fee-${fee.id}`}>Edit</FeeMenuItem>
          <FeeMenuItem
            onClick={onToggle}
            testId={`toggle-fee-${fee.id}`}
            destructive={fee.active}
          >
            {fee.active ? "Disable" : "Enable"}
          </FeeMenuItem>
        </div>,
        document.body,
      )}
    </>
  );
}

function FeeMenuItem({ children, onClick, testId, destructive }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      className={`block w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors duration-150 ${
        destructive ? "text-red-700" : "text-zinc-900"
      }`}
    >
      {children}
    </button>
  );
}
